import { useLocalStorage } from "@vueuse/core";
const NAVIGATION_CONTEXT_KEY = "extole_navigation_context";
const MAX_HISTORY_SIZE = 10;
class NavigationContextStore {
    constructor() {
        this.context = useLocalStorage(NAVIGATION_CONTEXT_KEY, {
            currentParent: null,
            history: [],
            lastMenuClick: null,
        });
    }
    getCurrentParent() {
        return this.context.value.currentParent;
    }
    getLastMenuClick() {
        return this.context.value.lastMenuClick;
    }
    recordNavigation(params) {
        const entry = {
            from: params.from,
            to: params.to,
            timestamp: Date.now(),
            menuItem: params.menuItem,
        };
        // Add to history
        this.context.value.history.unshift(entry);
        // Keep history size manageable
        if (this.context.value.history.length > MAX_HISTORY_SIZE) {
            this.context.value.history = this.context.value.history.slice(0, MAX_HISTORY_SIZE);
        }
        // Update current context
        if (params.menuItem) {
            this.context.value.currentParent = params.menuItem;
            this.context.value.lastMenuClick = params.menuItem;
        }
    }
    recordMenuClick(menuItem) {
        this.context.value.lastMenuClick = menuItem;
        this.context.value.currentParent = menuItem;
    }
    getContextForUrl(url) {
        // Check if we have a recent menu click that should override URL-based detection
        const recentEntry = this.context.value.history.find((entry) => entry.to === url && entry.menuItem);
        if (recentEntry && recentEntry.menuItem) {
            return recentEntry.menuItem;
        }
        // For non-navigation clicks, preserve the current context if it's still relevant
        if (this.context.value.lastMenuClick &&
            this.isUrlRelevantToMenu(url, this.context.value.lastMenuClick)) {
            return this.context.value.lastMenuClick;
        }
        // Check the navigation chain - if user navigated from a menu context
        // and the new URL is still relevant to that context, preserve it
        const lastMenuEntry = this.context.value.history.find((entry) => entry.menuItem);
        if (lastMenuEntry &&
            lastMenuEntry.menuItem &&
            this.isUrlRelevantToMenu(url, lastMenuEntry.menuItem)) {
            return lastMenuEntry.menuItem;
        }
        return null;
    }
    isUrlRelevantToMenu(url, menuItem) {
        const menuUrlPatterns = {
            programs: ["/programs", "/program", "/campaign-details"],
            performance: ["/account", "/program"],
            audiences: ["/audiences-overview"],
            rewards: ["/account-rewards"],
            reports: ["/reports"],
            partners: ["/integrations"],
            "tech-center": [
                "/tech-center",
                "/events/live",
                "/tech-center/batch-jobs",
                "/tech-center/tag-generator",
                "/tech-center/outbound-webhooks",
                "/tech-center/event-streams",
            ],
            "user-support": [
                "/profiles",
                "/events/approvals",
                "/events/live",
                "/create-event",
            ],
        };
        const patterns = menuUrlPatterns[menuItem] || [];
        return patterns.some((pattern) => url === pattern || url.startsWith(`${pattern}/`));
    }
    clearContext() {
        this.context.value = {
            currentParent: null,
            history: [],
            lastMenuClick: null,
        };
    }
    getHistory() {
        return [...this.context.value.history];
    }
}
const navigationContextStore = new NavigationContextStore();
// Global link interception for non-navigation clicks
let isGlobalListenerSetup = false;
const setupGlobalLinkInterception = () => {
    if (isGlobalListenerSetup)
        return;
    document.addEventListener("click", (event) => {
        const target = event.target;
        const link = target.closest("a[href]");
        if (link && link.href) {
            const { pathname, origin } = new URL(link.href);
            // Only track internal navigation (same origin)
            if (origin === window.location.origin) {
                // Don't override if this was a navigation component click
                // (those are handled by their own click handlers)
                const isNavClick = target.closest(".main-navigation, .inner-menu");
                if (!isNavClick) {
                    navigationContextStore.recordNavigation({
                        from: window.location.pathname,
                        to: pathname,
                        menuItem: null, // No specific menu item for non-nav clicks
                    });
                }
            }
        }
    });
    isGlobalListenerSetup = true;
};
export const useNavigationContext = () => {
    // Setup global listener on first use
    setupGlobalLinkInterception();
    return navigationContextStore;
};
